
import React, { useState, useEffect } from 'react';
import { calculateOffer, offerOptions } from '@/utils/calculations';
import OfferComparison from './OfferComparison';
import ClientDataForm from './ClientDataForm';
import { toast } from 'sonner';

// Import our new components
import CalculatorHeader from './calculator/CalculatorHeader';
import RoomAreaInputs from './calculator/RoomAreaInputs';
import ContactAdvisorCard from './calculator/ContactAdvisorCard';
import CalculateButton from './calculator/CalculateButton';

const PriceCalculator: React.FC = () => {
  const [rooms, setRooms] = useState<string>('');
  const [roomAreas, setRoomAreas] = useState<string[]>([]);
  const [installationType, setInstallationType] = useState<'home' | 'company'>('home');
  const [standardPrice, setStandardPrice] = useState<number>(0);
  const [premiumPrice, setPremiumPrice] = useState<number>(0);
  const [exclusivePrice, setExclusivePrice] = useState<number>(0);
  const [selectedOffer, setSelectedOffer] = useState<'standard' | 'premium' | 'exclusive' | null>(null);
  const [showOffers, setShowOffers] = useState<boolean>(false);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [showClientForm, setShowClientForm] = useState<boolean>(false);
  const [tooManyRooms, setTooManyRooms] = useState<boolean>(false);

  const handleRoomsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers
    if (value === '' || /^[0-9]+$/.test(value)) {
      setRooms(value);
      
      // Reset all offer-related states when changing rooms
      setSelectedOffer(null);
      setShowOffers(false);
      setShowClientForm(false);
      setTooManyRooms(false);
      
      const roomCount = parseInt(value, 10) || 0;
      
      // Check if too many rooms
      if (roomCount > 10) {
        setTooManyRooms(true);
        setRoomAreas([]);
      } else {
        setTooManyRooms(false);
        // Initialize room areas array with empty strings
        setRoomAreas(Array(roomCount).fill(''));
      }
    }
  };

  const handleRoomAreaChange = (index: number, value: string) => {
    // Allow numbers and decimal point
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      const newRoomAreas = [...roomAreas];
      newRoomAreas[index] = value;
      setRoomAreas(newRoomAreas);
    }
  };

  const calculatePrices = () => {
    if (!rooms) {
      toast("Uzupełnij liczbę pokoi", {
        description: "Wprowadź liczbę pokoi, aby kontynuować.",
      });
      return;
    }

    const roomsNum = parseInt(rooms, 10);
    
    if (roomsNum <= 0) {
      toast("Nieprawidłowa liczba pokoi", {
        description: "Liczba pokoi musi być większa od zera.",
      });
      return;
    }
    
    if (roomsNum > 10) {
      setTooManyRooms(true);
      return;
    }
    
    // Check if all room areas are filled
    const emptyAreaIndex = roomAreas.findIndex(area => area === '');
    if (emptyAreaIndex !== -1) {
      toast("Uzupełnij wszystkie powierzchnie", {
        description: `Wprowadź powierzchnię dla pokoju ${emptyAreaIndex + 1}.`,
      });
      return;
    }
    
    // Calculate total area
    const totalArea = roomAreas.reduce((sum, area) => sum + (parseFloat(area) || 0), 0);
    
    if (totalArea <= 0) {
      toast("Nieprawidłowe wartości", {
        description: "Powierzchnia musi być większa od zera.",
      });
      return;
    }

    setIsCalculating(true);
    setSelectedOffer(null);
    setShowClientForm(false);
    
    // Simulate calculation delay for UX
    setTimeout(() => {
      const standardPrice = calculateOffer(roomsNum, totalArea, 'standard', installationType);
      const premiumPrice = calculateOffer(roomsNum, totalArea, 'premium', installationType);
      const exclusivePrice = calculateOffer(roomsNum, totalArea, 'exclusive', installationType);
      
      setStandardPrice(standardPrice);
      setPremiumPrice(premiumPrice);
      setExclusivePrice(exclusivePrice);
      setShowOffers(true);
      setIsCalculating(false);
    }, 800);
  };

  const handleSelectOffer = (offer: 'standard' | 'premium' | 'exclusive') => {
    setSelectedOffer(offer);
    
    setTimeout(() => {
      setShowClientForm(true);
      
      // Scroll to the form
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 500);
    
    const offerName = offer === 'standard' ? 'Standard' : 
                      offer === 'premium' ? 'Premium' : 'Exclusive';
    
    toast(`Wybrano pakiet ${offerName}`, {
      description: "Teraz uzupełnij swoje dane do zamówienia.",
    });
  };

  const handleFormSubmitSuccess = () => {
    // Reset the calculator for a new calculation
    setRooms('');
    setRoomAreas([]);
    setSelectedOffer(null);
    setShowOffers(false);
    setShowClientForm(false);
    setTooManyRooms(false);
    
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="space-y-8 opacity-0 animate-fadeIn">
        {/* Input Form */}
        <div className="glass-card p-6 md:p-8">
          <div className="space-y-6">
            <CalculatorHeader 
              installationType={installationType}
              onInstallationTypeChange={setInstallationType}
              rooms={rooms}
              onRoomsChange={handleRoomsChange}
            />
            
            {/* Contact Advisor Card - shown when too many rooms */}
            {tooManyRooms && <ContactAdvisorCard />}
            
            {/* Room Areas Inputs - shown when rooms <= 10 */}
            {!tooManyRooms && roomAreas.length > 0 && (
              <RoomAreaInputs 
                roomAreas={roomAreas} 
                onRoomAreaChange={handleRoomAreaChange} 
              />
            )}
            
            <CalculateButton 
              onClick={calculatePrices}
              disabled={isCalculating || tooManyRooms}
              isCalculating={isCalculating}
            />
          </div>
        </div>
        
        {/* Offers Display */}
        {showOffers && (
          <div className="opacity-0 animate-fadeIn">
            <h2 className="text-2xl font-semibold text-center mb-4">Twoje oferty</h2>
            <OfferComparison
              standardPrice={standardPrice}
              premiumPrice={premiumPrice}
              exclusivePrice={exclusivePrice}
              standardDetails={offerOptions.standard}
              premiumDetails={offerOptions.premium}
              exclusiveDetails={offerOptions.exclusive}
              selectedOffer={selectedOffer}
              onSelectOffer={handleSelectOffer}
            />
          </div>
        )}
        
        {/* Client Data Form */}
        {showClientForm && selectedOffer && (
          <ClientDataForm 
            selectedOffer={selectedOffer} 
            offerPrice={selectedOffer === 'standard' ? standardPrice : 
                        selectedOffer === 'premium' ? premiumPrice : exclusivePrice}
            onSubmitSuccess={handleFormSubmitSuccess}
            numberOfRooms={parseInt(rooms, 10)}
            roomAreas={roomAreas.map(area => parseFloat(area) || 0)}
            installationType={installationType}
          />
        )}
      </div>
    </div>
  );
};

export default PriceCalculator;
